import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  listMeituanReservationOrderLogs,
  type MeituanReservationOrderLog as MeituanReservationOrderLogRecord,
} from '../services/meituanReservationLogService';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const processStatusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待处理', color: 'orange' },
  1: { text: '已处理', color: 'green' },
  2: { text: '处理失败', color: 'red' },
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value;
};

const formatJSON = (value?: unknown) => {
  if (!value) return '-';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  return JSON.stringify(value, null, 2);
};

const MeituanReservationOrderLog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<MeituanReservationOrderLogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meituanOrderId, setMeituanOrderId] = useState('');
  const [productName, setProductName] = useState('');
  const [userName, setUserName] = useState('');
  const [mobile, setMobile] = useState('');
  const [processStatus, setProcessStatus] = useState<string>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentLog, setCurrentLog] = useState<MeituanReservationOrderLogRecord | null>(null);

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await listMeituanReservationOrderLogs({
        page: nextPage,
        pageSize: nextPageSize,
        meituanOrderId: meituanOrderId || undefined,
        productName: productName || undefined,
        userName: userName || undefined,
        mobile: mobile || undefined,
        processStatus,
        startTime: dateRange?.[0]?.format('YYYY-MM-DD 00:00:00'),
        endTime: dateRange?.[1]?.format('YYYY-MM-DD 23:59:59'),
      });
      setLogs(res.data.list || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('加载美团预约订单日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    void loadData(1, pageSize);
  };

  const handleReset = () => {
    setMeituanOrderId('');
    setProductName('');
    setUserName('');
    setMobile('');
    setProcessStatus(undefined);
    setDateRange(null);
    setPage(1);
    setTimeout(() => void loadData(1, pageSize), 0);
  };

  const columns: ColumnsType<MeituanReservationOrderLogRecord> = [
    {
      title: '美团订单ID',
      dataIndex: 'meituanOrderId',
      key: 'meituanOrderId',
      width: 160,
      ellipsis: true,
      render: value => <Tooltip title={value || '-'}><Text>{value || '-'}</Text></Tooltip>,
    },
    { title: '产品名称', dataIndex: 'productName', key: 'productName', width: 150, ellipsis: true, render: value => value || '-' },
    { title: '联系人', dataIndex: 'userName', key: 'userName', width: 110, render: value => value || '-' },
    {
      title: '联系电话',
      key: 'phone',
      width: 140,
      render: (_, record) => record.phone || record.mobile || '-',
    },
    {
      title: '地址',
      dataIndex: 'userAddress',
      key: 'userAddress',
      width: 260,
      ellipsis: true,
      render: value => value ? <Tooltip title={value}><Text>{value}</Text></Tooltip> : '-',
    },
    {
      title: '预约时间',
      key: 'reserveTime',
      width: 250,
      render: (_, record) => `${record.beginTime || '-'} 至 ${record.endTime || '-'}`,
    },
    { title: '金额', dataIndex: 'amount', key: 'amount', width: 100, render: value => value || '-' },
    {
      title: '处理状态',
      dataIndex: 'processStatus',
      key: 'processStatus',
      width: 120,
      render: value => {
        const item = processStatusMap[value] || { text: value ?? '-', color: 'default' };
        return <Tag color={item.color}>{item.text}</Tag>;
      },
    },
    {
      title: '处理说明',
      dataIndex: 'processMessage',
      key: 'processMessage',
      width: 260,
      ellipsis: true,
      render: value => value ? <Tooltip title={value}><Text>{value}</Text></Tooltip> : '-',
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: value => formatDateTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setCurrentLog(record);
            setDetailVisible(true);
          }}
        >
          详情
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>美团预约订单日志</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input placeholder="美团订单ID" value={meituanOrderId} onChange={event => setMeituanOrderId(event.target.value)} allowClear style={{ width: 180 }} />
          <Input placeholder="产品名称" value={productName} onChange={event => setProductName(event.target.value)} allowClear style={{ width: 160 }} />
          <Input placeholder="联系人" value={userName} onChange={event => setUserName(event.target.value)} allowClear style={{ width: 140 }} />
          <Input placeholder="手机号" value={mobile} onChange={event => setMobile(event.target.value)} allowClear style={{ width: 140 }} />
          <Select placeholder="处理状态" value={processStatus} onChange={setProcessStatus} allowClear style={{ width: 120 }}>
            <Select.Option value="0">待处理</Select.Option>
            <Select.Option value="1">已处理</Select.Option>
            <Select.Option value="2">处理失败</Select.Option>
          </Select>
          <RangePicker value={dateRange} onChange={setDateRange} />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
        </Space>
      </Card>
      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={logs}
          scroll={{ x: 1500 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: value => `共 ${value} 条`,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize || 10);
            },
          }}
        />
      </Card>
      <Modal title="美团预约订单日志详情" open={detailVisible} onCancel={() => setDetailVisible(false)} footer={null} width={900}>
        {currentLog && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="美团订单ID">{currentLog.meituanOrderId || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理状态">{processStatusMap[currentLog.processStatus]?.text || currentLog.processStatus}</Descriptions.Item>
              <Descriptions.Item label="产品名称">{currentLog.productName || '-'}</Descriptions.Item>
              <Descriptions.Item label="联系人">{currentLog.userName || '-'}</Descriptions.Item>
              <Descriptions.Item label="预约开始时间">{currentLog.beginTime || '-'}</Descriptions.Item>
              <Descriptions.Item label="预约结束时间">{currentLog.endTime || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentLog.mobile || '-'}</Descriptions.Item>
              <Descriptions.Item label="隐私号">{currentLog.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>{currentLog.userAddress || '-'}</Descriptions.Item>
              <Descriptions.Item label="用户备注" span={2}>{currentLog.comment || '-'}</Descriptions.Item>
              <Descriptions.Item label="订单备注" span={2}>{currentLog.orderRemark || '-'}</Descriptions.Item>
              <Descriptions.Item label="处理说明" span={2}>{currentLog.processMessage || '-'}</Descriptions.Item>
            </Descriptions>
            <Card title="原始业务参数" size="small">
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{formatJSON(currentLog.rawPayload)}</pre>
            </Card>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default MeituanReservationOrderLog;
